/* DirectoryTab — company phone directory + admin extension assignment.
 *
 * Everyone: search staff → click-to-call their extension, or warm-transfer an
 * active call to them. Shows the caller's own extension.
 * Admins: "Manage extensions" mode to individually assign a 3-digit extension
 * (auto-suggested next-free) + cell (for the ring-my-cell fallback) + availability.
 */
import { useEffect, useState, useCallback } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Phone, PhoneForwarded, Search, Hash, Smartphone, MonitorSmartphone,
  Settings2, Check, X, Loader2, Sparkles, UserPlus, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => {
  const t = localStorage.getItem("token");
  return { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) };
};

const initials = (n) => (n || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

export default function DirectoryTab({ call, fromNumber, isAdmin }) {
  const [entries, setEntries] = useState([]);
  const [myExt, setMyExt] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [manage, setManage] = useState(false);

  const loadDirectory = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/phone/directory`, { headers: hdrs() });
      const d = await r.json();
      setEntries(d.entries || []);
      setMyExt(d.my_extension || "");
    } catch { /* noop */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadDirectory(); }, [loadDirectory]);

  const activeCall = call?.activeCall;
  const dial = (ext) => {
    if (!ext) return;
    call?.makeCall(ext, fromNumber);
    toast.success(`Calling extension ${ext}…`);
  };
  const transfer = (e) => {
    call?.warmTransferToColleague?.({
      id: e.user_id, first_name: e.first_name, last_name: e.last_name, email: e.email,
    });
  };

  const filtered = entries.filter((e) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return `${e.name} ${e.extension} ${e.department} ${e.role}`.toLowerCase().includes(s);
  });

  if (manage && isAdmin) {
    return <ManageExtensions onBack={() => { setManage(false); loadDirectory(); }} />;
  }

  return (
    <div className="space-y-4" data-testid="directory-tab">
      {/* My extension + manage */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="rounded-xl px-4 py-2.5 bg-[#F0F5FF] border border-[#D6E4FF]" data-testid="directory-my-ext">
          {myExt ? (
            <div className="flex items-center gap-2 text-[#032866]">
              <Hash className="w-4 h-4 text-[#0866FF]" />
              <span className="text-xs font-semibold">Your extension</span>
              <span className="text-lg font-black tabular-nums">{myExt}</span>
            </div>
          ) : (
            <span className="text-xs font-semibold text-[#7B8FA3]">You don't have an extension yet — ask an admin to assign one.</span>
          )}
        </div>
        {isAdmin && (
          <Button variant="outline" onClick={() => setManage(true)} className="h-9 text-xs" data-testid="directory-manage-toggle">
            <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Manage extensions
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, extension, department…"
          className="pl-9 h-10" data-testid="directory-search" />
      </div>

      {/* List */}
      {loading ? (
        <div className="py-10 text-center text-[#94A3B8]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center text-[13px] text-[#7B8FA3]" data-testid="directory-empty">
          {entries.length === 0 ? "No extensions assigned yet." : "No matches."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="directory-grid">
          {filtered.map((e) => {
            const pColor = e.available ? "#1D9E75" : "#94A3B8";
            const pLabel = e.available ? "Available" : "Unavailable";
            return (
              <div key={e.user_id}
                className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`directory-row-${e.extension}`}>
                <div className="relative shrink-0">
                  <div className="w-12 h-12 rounded-full grid place-items-center text-white text-sm font-black"
                    style={{ background: "#032866", boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${pColor}` }}>
                    {initials(e.name)}
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white"
                    style={{ background: pColor }} data-testid={`directory-presence-${e.extension}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-bold text-[#032866] truncate">{e.name}</div>
                  <div className="flex items-center gap-1.5 text-[11px] flex-wrap">
                    <span className="font-bold" style={{ color: pColor }}>{pLabel}</span>
                    {e.department ? <span className="text-slate-300">·</span> : null}
                    {e.department ? <span className="text-[#7B8FA3] truncate">{e.department}</span> : null}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Hash className="w-3 h-3 text-[#0866FF]" />
                    <span className="text-sm font-black tabular-nums text-[#0866FF]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{e.extension || "—"}</span>
                    {e.has_cell && <Smartphone className="w-3 h-3 text-[#94A3B8]" title="Cell on file (fallback)" />}
                    {e.has_deskphone && <MonitorSmartphone className="w-3 h-3 text-[#94A3B8]" title="Desk phone linked" />}
                  </div>
                </div>
                <div className="shrink-0">
                  {activeCall ? (
                    <Button size="sm" variant="outline" onClick={() => transfer(e)} className="h-10 w-10 p-0 rounded-full" title="Transfer call here" data-testid={`directory-transfer-${e.extension}`}>
                      <PhoneForwarded className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => dial(e.extension)} disabled={!e.extension} className="h-10 w-10 p-0 rounded-full bg-[#10B981] hover:bg-[#059669]" title={`Call extension ${e.extension}`} data-testid={`directory-call-${e.extension}`}>
                      <Phone className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Admin: assign / edit / remove extensions ─────────────────────── */
function ManageExtensions({ onBack }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [drafts, setDrafts] = useState({});     // user_id -> {extension, cell_phone, call_available}
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/phone/directory/all-staff`, { headers: hdrs() });
      const d = await r.json();
      const rows = d.entries || [];
      setStaff(rows);
      const init = {};
      rows.forEach((s) => { init[s.user_id] = { extension: s.extension || "", cell_phone: s.cell_phone || "", call_available: s.available }; });
      setDrafts(init);
    } catch { /* noop */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (id, k, v) => setDrafts((d) => ({ ...d, [id]: { ...d[id], [k]: v } }));

  const autoExt = async (id) => {
    try {
      const r = await fetch(`${API}/api/phone/extensions/next`, { headers: hdrs() });
      const d = await r.json();
      setField(id, "extension", d.extension);
    } catch { toast.error("Couldn't fetch next extension"); }
  };

  const save = async (id) => {
    const d = drafts[id];
    setSavingId(id);
    try {
      const res = await fetch(`${API}/api/phone/users/${id}/extension`, {
        method: "POST", headers: hdrs(),
        body: JSON.stringify({ extension: d.extension || null, cell_phone: d.cell_phone, call_available: d.call_available }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Save failed"); }
      const saved = await res.json();
      toast.success(`Extension ${saved.extension} saved`);
      load();
    } catch (e) { toast.error(String(e.message || e)); }
    setSavingId(null);
  };

  const remove = async (id) => {
    setSavingId(id);
    try {
      await fetch(`${API}/api/phone/users/${id}/extension`, { method: "DELETE", headers: hdrs() });
      toast.success("Extension removed");
      load();
    } catch { toast.error("Remove failed"); }
    setSavingId(null);
  };

  const filtered = staff.filter((s) => {
    const t = q.trim().toLowerCase();
    return !t || `${s.name} ${s.email} ${s.role} ${s.extension}`.toLowerCase().includes(t);
  });

  return (
    <div className="space-y-4" data-testid="directory-manage">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[#032866] font-bold text-sm"><UserPlus className="w-4 h-4" /> Assign extensions</div>
        <Button variant="outline" onClick={onBack} className="h-8 text-xs" data-testid="directory-manage-back"><X className="w-3.5 h-3.5 mr-1" /> Done</Button>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff…" className="pl-9 h-10" data-testid="directory-manage-search" />
      </div>
      {loading ? (
        <div className="py-10 text-center text-[#94A3B8]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : (
        <div className="space-y-2">
          {filtered.map((s) => {
            const d = drafts[s.user_id] || {};
            return (
              <div key={s.user_id} className="rounded-xl border border-slate-100 p-3 bg-white" data-testid={`directory-assign-${s.user_id}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-[#032866] text-white flex items-center justify-center text-[10px] font-black">{initials(s.name)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-bold text-[#032866] truncate">{s.name}</div>
                    <div className="text-[10px] text-[#7B8FA3] capitalize">{(s.role || "").replace(/_/g, " ")}{s.department ? ` · ${s.department}` : ""}</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                  <div className="sm:col-span-3 flex gap-1">
                    <Input value={d.extension || ""} onChange={(e) => setField(s.user_id, "extension", e.target.value.replace(/\D/g, "").slice(0, 5))}
                      placeholder="Ext" className="h-9 text-sm" data-testid={`directory-ext-input-${s.user_id}`} />
                    <Button size="sm" variant="outline" onClick={() => autoExt(s.user_id)} className="h-9 px-2 text-[10px] shrink-0" title="Next free" data-testid={`directory-auto-${s.user_id}`}>
                      <Sparkles className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="sm:col-span-4">
                    <Input value={d.cell_phone || ""} onChange={(e) => setField(s.user_id, "cell_phone", e.target.value)}
                      placeholder="Cell (fallback)" className="h-9 text-sm" data-testid={`directory-cell-input-${s.user_id}`} />
                  </div>
                  <label className="sm:col-span-2 flex items-center gap-1.5 text-[11px] font-semibold text-[#475569] cursor-pointer">
                    <input type="checkbox" checked={d.call_available !== false} onChange={(e) => setField(s.user_id, "call_available", e.target.checked)}
                      data-testid={`directory-avail-${s.user_id}`} /> Available
                  </label>
                  <div className="sm:col-span-3 flex gap-1 justify-end">
                    <Button size="sm" onClick={() => save(s.user_id)} disabled={savingId === s.user_id} className="h-9 text-xs bg-[#032866]" data-testid={`directory-save-${s.user_id}`}>
                      {savingId === s.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" />Save</>}
                    </Button>
                    {s.extension && (
                      <Button size="sm" variant="outline" onClick={() => remove(s.user_id)} className="h-9 px-2 text-red-500 border-red-200" title="Remove extension" data-testid={`directory-remove-${s.user_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
